/** Ephemeral presence helpers (spec §8.12): block focus indicators per peer. */
import type { EphemeralStore } from "loro-crdt";

export interface FocusState {
  userId: string;
  name: string;
  color: string;
  treeId: string | null;
}

export function setFocus(store: EphemeralStore, peerId: string, state: FocusState): void {
  store.set(`focus:${peerId}`, state as unknown as Record<string, string | null>);
}

export function allFocus(store: EphemeralStore, exceptPeer?: string): Array<FocusState & { peer: string }> {
  const out: Array<FocusState & { peer: string }> = [];
  for (const key of store.keys()) {
    if (!key.startsWith("focus:")) continue;
    const peer = key.slice(6);
    if (peer === exceptPeer) continue;
    const v = store.get(key) as unknown as FocusState | undefined;
    if (v) out.push({ ...v, peer });
  }
  return out;
}

/** The part of loro-prosemirror's `CursorEphemeralStore` the bridge uses. */
interface CursorStore {
  get(key: string): unknown;
  keys(): string[];
  apply(bytes: Uint8Array): void;
  delete(key: string): void;
  subscribeLocalUpdates(f: (bytes: Uint8Array) => void): () => void;
}

const isPeerKey = (key: string) => /^\d+$/.test(key);

/**
 * Text cursors travel in the document's presence room (spec §8.12, 0.49): the page's cursor store
 * is bridged into the presence store, which is the one with a sync connection. The reader's own entry
 * (key = peer id) is copied into presence on every local change; imported peer-id entries are applied
 * back into the cursor store, so the ProseMirror cursor plugin draws them. `focus:` keys never cross,
 * and an imported entry is applied, not set, so nothing echoes back. `user` names the caret others
 * see. Returns the unsubscribe.
 */
export function bridgeCursors(presence: EphemeralStore, cursors: CursorStore, peer: string, user: { name: string; color: string }): () => void {
  const copyOwn = () => {
    const own = cursors.get(peer) as Record<string, unknown> | undefined;
    // the caret is labelled with the reader's name, in their colour (their account's, not a token)
    if (own !== undefined) presence.set(peer, { ...own, user } as unknown as Record<string, string | null>);
    else if (presence.get(peer) !== undefined) presence.delete(peer); // blurred: the caret disappears
  };
  const importPeer = (key: string) => {
    if (isPeerKey(key) && key !== peer) cursors.apply(presence.encode(key));
  };
  for (const key of presence.keys()) importPeer(key);
  copyOwn();
  const offLocal = cursors.subscribeLocalUpdates(copyOwn);
  const offPresence = presence.subscribe((e) => {
    if (e.by === "local") return;
    for (const key of [...e.added, ...e.updated]) importPeer(key);
    for (const key of e.removed) if (isPeerKey(key) && key !== peer) cursors.delete(key);
  });
  return () => {
    offLocal();
    offPresence();
  };
}
